// src/pages/analytics/LostBusinessTab.tsx
//
// Lost Business — churn finder (recipe: sales-gaps). Three modes:
//   customers    — "Stopped buying": customers with no purchases in the last
//                  N days who WERE buying during the prior M days.
//   items        — "Dropped items": customer+item pairs that went quiet.
//   never_bought — "Never bought…": customers who never bought a target item
//                  (targetItem is REQUIRED — the query stays idle until set).
//
// lastSale is a plain yyyy-MM-dd string from the agent — rendered as-is.

import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  UserMinus, RefreshCw, Download, FileSpreadsheet, ChevronLeft, ChevronRight, AlertTriangle, Search,
} from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import type {
  SalesGapsMode, SalesGapCustomerRow, SalesGapItemRow, SalesGapNeverBoughtRow,
} from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { SalesFilterBar, EMPTY_SALES_FILTERS, salesFilterParams, type SharedSalesFilterState } from './salesFilterBar';

const PAGE_SIZE = 100;

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const MODES: { value: SalesGapsMode; label: string }[] = [
  { value: 'customers',    label: 'Stopped buying' },
  { value: 'items',        label: 'Dropped items' },
  { value: 'never_bought', label: 'Never bought…' },
];

const CUSTOMER_COLS: CsvColumns<SalesGapCustomerRow> = [
  ['Customer',       (r) => r.customerCode],
  ['Customer Name',  (r) => r.customerName],
  ['Salesman',       (r) => r.salesman],
  ['Category',       (r) => r.category],
  ['Last Sale',      (r) => r.lastSale ?? ''],
  ['Days Since',     (r) => r.daysSinceLast],
  ['Prior $',        (r) => r.priorDollars],
];
const ITEM_COLS: CsvColumns<SalesGapItemRow> = [
  ['Customer',       (r) => r.customerCode],
  ['Customer Name',  (r) => r.customerName],
  ['Item',           (r) => r.itemNo],
  ['Description',    (r) => r.description],
  ['Last Sale',      (r) => r.lastSale ?? ''],
  ['Days Since',     (r) => r.daysSinceLast],
  ['Prior Qty',      (r) => r.priorQty],
  ['Prior $',        (r) => r.priorDollars],
];
const NEVER_COLS: CsvColumns<SalesGapNeverBoughtRow> = [
  ['Customer',       (r) => r.customerCode],
  ['Customer Name',  (r) => r.customerName],
  ['Salesman',       (r) => r.salesman],
  ['Category',       (r) => r.category],
  ['Last Sale',      (r) => r.lastSale ?? ''],
  ['Total $',        (r) => r.totalDollars],
];

export function LostBusinessTab() {
  const [mode, setMode] = useState<SalesGapsMode>('customers');
  const [recentDays, setRecentDays] = useState(60);
  const [priorDays, setPriorDays] = useState(365);
  const [targetItem, setTargetItem] = useState('');
  const [shared, setShared] = useState<SharedSalesFilterState>(EMPTY_SALES_FILTERS);
  const [offset, setOffset] = useState(0);

  // Any filter/mode change restarts paging at 0.
  const withReset = <T,>(setter: (v: T) => void) => (v: T) => { setOffset(0); setter(v); };

  const { data: filters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });

  // never_bought requires a target item — don't fire the recipe without one.
  const ready = mode !== 'never_bought' || targetItem.trim().length > 0;
  // item/itemCategory only apply to customers + items modes.
  const showItemFilters = mode !== 'never_bought';

  const q = useQuery({
    queryKey: ['sales-gaps', mode, recentDays, priorDays, targetItem, shared, offset],
    queryFn: () => {
      const sharedParams = salesFilterParams(shared);
      if (!showItemFilters) { sharedParams.item = undefined; sharedParams.itemCategory = undefined; sharedParams.itemSubcategory = undefined; }
      return api.getSalesGaps({
        mode,
        recentDays: String(recentDays),
        priorDays:  String(priorDays),
        targetItem: mode === 'never_bought' ? targetItem.trim() : undefined,
        limit:      String(PAGE_SIZE),
        offset:     String(offset),
        ...sharedParams,
      });
    },
    enabled: ready,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const rows = data?.rows ?? [];
  const totalRows = data?.page.totalRows ?? 0;
  const returned = data?.page.returned ?? 0;

  const exportRows = () => {
    if (mode === 'customers') exportCsv('lost-business-customers', CUSTOMER_COLS, rows as SalesGapCustomerRow[]);
    else if (mode === 'items') exportCsv('lost-business-items', ITEM_COLS, rows as SalesGapItemRow[]);
    else exportCsv('lost-business-never-bought', NEVER_COLS, rows as SalesGapNeverBoughtRow[]);
  };

  const exportRowsXlsx = () => {
    if (mode === 'customers') exportXlsx('lost-business-customers', CUSTOMER_COLS, rows as SalesGapCustomerRow[]);
    else if (mode === 'items') exportXlsx('lost-business-items', ITEM_COLS, rows as SalesGapItemRow[]);
    else exportXlsx('lost-business-never-bought', NEVER_COLS, rows as SalesGapNeverBoughtRow[]);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <UserMinus size={20} /> Lost Business
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Customers who stopped buying, items they dropped, and who never bought a target item.
        </p>
      </div>

      {/* Mode tabs */}
      <div className="flex items-center gap-1 border-b border-slate-700/40 overflow-x-auto md:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {MODES.map(m => (
          <button key={m.value} onClick={() => withReset(setMode)(m.value)}
            className={`shrink-0 whitespace-nowrap px-3 py-2 text-sm transition-all border-b-2 -mb-px ${
              mode === m.value
                ? 'text-white border-blue-500'
                : 'text-slate-400 border-transparent hover:text-slate-200'
            }`}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        {mode === 'never_bought' ? (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Target Item #</span>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
              <input type="text" value={targetItem} onChange={e => withReset(setTargetItem)(e.target.value)}
                placeholder="e.g. 1011"
                className="pl-7 pr-2.5 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50 w-[150px]" />
            </div>
            <span className="text-[10px] text-slate-500">Shows customers who never bought this item</span>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">No purchases in last</span>
              <div className="flex items-center gap-1.5">
                <input type="number" min={1} max={999} value={recentDays}
                  onChange={e => withReset(setRecentDays)(Math.max(1, Number(e.target.value) || 0))}
                  className="px-2.5 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 w-[70px]" />
                <span className="text-xs text-slate-500">days</span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Was buying during the prior</span>
              <div className="flex items-center gap-1.5">
                <input type="number" min={1} max={9999} value={priorDays}
                  onChange={e => withReset(setPriorDays)(Math.max(1, Number(e.target.value) || 0))}
                  className="px-2.5 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 w-[70px]" />
                <span className="text-xs text-slate-500">days</span>
              </div>
            </div>
          </>
        )}
        <SalesFilterBar
          value={shared}
          onChange={withReset(setShared)}
          filters={filters}
          showItemFilters={showItemFilters}
        />
        <div className="flex-1" />
        <button
          onClick={exportRows}
          disabled={rows.length === 0}
          title="Export the current page to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={exportRowsXlsx}
          disabled={rows.length === 0}
          title="Export the current page to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={!ready || q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Window note (server-provided caption) */}
      {data?.windowNote && (
        <p className="text-[11px] text-slate-500">{data.windowNote}</p>
      )}

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load results</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {!ready ? (
        <div className="p-8 text-center text-slate-500 text-sm border border-slate-800 rounded-lg">
          Enter an item number above to find customers who never bought it.
        </div>
      ) : q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            {mode === 'customers' && <CustomersTable rows={rows as SalesGapCustomerRow[]} />}
            {mode === 'items' && <ItemsTable rows={rows as SalesGapItemRow[]} />}
            {mode === 'never_bought' && <NeverBoughtTable rows={rows as SalesGapNeverBoughtRow[]} />}
          </div>
          {rows.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">
              {mode === 'never_bought' ? 'Every matching customer has bought this item' : 'No lost business found — nice'}
            </div>
          )}
          {/* Pager */}
          <div className="px-3 py-2 border-t border-slate-700/30 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {returned > 0 ? `Showing ${offset + 1}–${offset + returned} of ${fmtNum(totalRows)}` : 'No rows'}
              {q.isFetching && !q.isLoading && ' · refreshing…'}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || q.isFetching}
                className="px-3 py-2.5 md:px-2 md:py-1.5 rounded-md text-xs bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white disabled:opacity-40 flex items-center gap-1">
                <ChevronLeft size={12} /> Prev
              </button>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= totalRows || q.isFetching}
                className="px-3 py-2.5 md:px-2 md:py-1.5 rounded-md text-xs bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white disabled:opacity-40 flex items-center gap-1">
                Next <ChevronRight size={12} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Per-mode tables ─────────────────────────────────────────────────

const TH = 'px-3 py-2.5 text-slate-400 font-medium';

function CustomersTable({ rows }: { rows: SalesGapCustomerRow[] }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-slate-700/50 bg-slate-800/50">
          <th className={`${TH} text-left`}>Code</th>
          <th className={`${TH} text-left`}>Customer</th>
          <th className={`${TH} text-left`}>Salesman</th>
          <th className={`${TH} text-left`}>Category</th>
          <th className={`${TH} text-left`}>Last Sale</th>
          <th className={`${TH} text-right`}>Days Since</th>
          <th className={`${TH} text-right`}>Prior $</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.customerCode} className="border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
            <td className="px-3 py-2 font-mono text-slate-500">{r.customerCode}</td>
            <td className="px-3 py-2 text-white font-medium max-w-[220px] truncate">{r.customerName}</td>
            <td className="px-3 py-2 text-slate-400">{r.salesman}</td>
            <td className="px-3 py-2 text-slate-400">{r.category}</td>
            <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{r.lastSale ?? '—'}</td>
            <td className="px-3 py-2 text-right text-amber-300">{fmtNum(r.daysSinceLast)}</td>
            <td className="px-3 py-2 text-right text-slate-300">{fmtCur(r.priorDollars)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ItemsTable({ rows }: { rows: SalesGapItemRow[] }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-slate-700/50 bg-slate-800/50">
          <th className={`${TH} text-left`}>Code</th>
          <th className={`${TH} text-left`}>Customer</th>
          <th className={`${TH} text-left`}>Item</th>
          <th className={`${TH} text-left`}>Description</th>
          <th className={`${TH} text-left`}>Last Sale</th>
          <th className={`${TH} text-right`}>Days Since</th>
          <th className={`${TH} text-right`}>Prior Qty</th>
          <th className={`${TH} text-right`}>Prior $</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={`${r.customerCode}-${r.itemNo}`} className="border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
            <td className="px-3 py-2 font-mono text-slate-500">{r.customerCode}</td>
            <td className="px-3 py-2 text-white font-medium max-w-[200px] truncate">{r.customerName}</td>
            <td className="px-3 py-2 font-mono text-slate-300">{r.itemNo}</td>
            <td className="px-3 py-2 text-white max-w-[200px] truncate">{r.description}</td>
            <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{r.lastSale ?? '—'}</td>
            <td className="px-3 py-2 text-right text-amber-300">{fmtNum(r.daysSinceLast)}</td>
            <td className="px-3 py-2 text-right text-slate-300">{fmtNum(r.priorQty)}</td>
            <td className="px-3 py-2 text-right text-slate-300">{fmtCur(r.priorDollars)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NeverBoughtTable({ rows }: { rows: SalesGapNeverBoughtRow[] }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-slate-700/50 bg-slate-800/50">
          <th className={`${TH} text-left`}>Code</th>
          <th className={`${TH} text-left`}>Customer</th>
          <th className={`${TH} text-left`}>Salesman</th>
          <th className={`${TH} text-left`}>Category</th>
          <th className={`${TH} text-left`}>Last Sale (any item)</th>
          <th className={`${TH} text-right`}>Total $</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.customerCode} className="border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
            <td className="px-3 py-2 font-mono text-slate-500">{r.customerCode}</td>
            <td className="px-3 py-2 text-white font-medium max-w-[220px] truncate">{r.customerName}</td>
            <td className="px-3 py-2 text-slate-400">{r.salesman}</td>
            <td className="px-3 py-2 text-slate-400">{r.category}</td>
            <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{r.lastSale ?? '—'}</td>
            <td className="px-3 py-2 text-right text-slate-300">{fmtCur(r.totalDollars)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
